import { lazy, Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router';

import LandingPage from '!views/LandingPage';
import { TrlStockExchangeMarketDetails } from '!views/Market/StockExchange';
import { TradingFundsDetails } from '!views/Trading/Funds/TradingFundsDetails';
import { TradingStockExchangeDetails } from '!views/Trading/StockExchange/TradingStockExchangeDetails';
import { getActiveMenuItemSelector } from '»components/Menus';
import { TrlInsertOrderPrimary } from '»components/templates/TrlInsertOrder';
import { TitlesViewContextProvider } from '»utils/contexts';
import { useUserAuthentication } from '»utils/customHooks';

const Dashboard = lazy(
  () => import(/* webpackChunkName: "Dashboard" */ '!views/Dashboard'),
);
const Settings = lazy(
  () => import(/* webpackChunkName: "Settings" */ '!views/Settings'),
);
const MarketPrimary = lazy(
  () => import(/* webpackChunkName: "MarketPrimary" */ '!views/Market/Primary'),
);
const MarketStockExchange = lazy(
  () =>
    import(
      /* webpackChunkName: "MarketStockExchange" */ '!views/Market/StockExchange'
    ),
);
const Funds = lazy(
  () => import(/* webpackChunkName: "Funds" */ '!views/Market/Funds'),
);
const TrlFundsManagerDetails = lazy(
  () =>
    import(
      /* webpackChunkName: "Funds" */ '!views/Market/Funds/Manager/TrlFundsManagerDetails'
    ),
);
const Wallet = lazy(
  () => import(/* webpackChunkName: "Wallet" */ '!views/Wallet'),
);
const TradingPrimary = lazy(
  () =>
    import(/* webpackChunkName: "TradingPrimary" */ '!views/Trading/Primary'),
);
const TradingStockExchange = lazy(
  () =>
    import(
      /* webpackChunkName: "TradingStockExchange" */ '!views/Trading/StockExchange'
    ),
);

const TradingFunds = lazy(
  () => import(/* webpackChunkName: "TradingFunds" */ '!views/Trading/Funds'),
);
const TradingProofPreview = lazy(
  () =>
    import(
      /* webpackChunkName: "TrlProofPreview" */ '»components/molecules/TrlProofPreview'
    ),
);
const ConfigureAccounts = lazy(
  () =>
    import(
      /* webpackChunkName: "ConfigureAccounts" */ '!views/Settings/Mobile/ConfigureAccounts'
    ),
);
const Email = lazy(
  () => import(/* webpackChunkName: "Email" */ '!views/Settings/Mobile/Email'),
);
const Contact = lazy(
  () =>
    import(/* webpackChunkName: "Contact" */ '!views/Settings/Mobile/Contact'),
);
const OTPDeliveryMethod = lazy(
  () =>
    import(
      /* webpackChunkName: "OTPDeliveryMethod" */ '!views/Settings/Mobile/OTPDeliveryMethod'
    ),
);
const ChangePassword = lazy(
  () =>
    import(
      /* webpackChunkName: "ChangePassword" */ '!views/Settings/Mobile/ChangePassword'
    ),
);
const QuestionnaireMobile = lazy(
  () =>
    import(
      /* webpackChunkName: "QuestionnaireMobile" */ '!views/Settings/Mobile/QuestionnaireMobile'
    ),
);
const ChooseLanguage = lazy(
  () =>
    import(
      /* webpackChunkName: "ChooseLanguage" */ '!views/Settings/Mobile/ChooseLanguage'
    ),
);
const CustomerSupport = lazy(
  () =>
    import(
      /* webpackChunkName: "CustomerSupport" */ '!views/Settings/Mobile/CustomerSupport'
    ),
);
const Onboarding = lazy(
  () =>
    import(/* webpackChunkName: "Onboarding" */ '»components/Auth/Onboarding'),
);
const Login = lazy(
  () => import(/* webpackChunkName: "Login" */ '»components/Auth/Login'),
);

export const AppBody = () => {
  const location = useLocation();

  const activeMenuItem = useSelector(getActiveMenuItemSelector);

  const { isUserAuthenticated } = useUserAuthentication();

  const controlF5Redirect = () => {
    switch (activeMenuItem) {
      case 'DASHBOARD':
        return <Dashboard />;
      case 'MARKET:PRIMARY':
        return <MarketPrimary />;
      case 'MARKET:BOLSA':
        return (
          <TitlesViewContextProvider>
            <MarketStockExchange />
          </TitlesViewContextProvider>
        );
      case 'MARKET:BOLSA:DETAILS':
        return <TrlStockExchangeMarketDetails />;
      case 'MARKET:FUNDS':
        return <Funds />;
      case 'MARKET:FUNDS:DETAILS':
        return <TrlFundsManagerDetails />;
      case 'TRADING:PRIMARY':
        return <TradingPrimary />;
      case 'TRADING:BOLSA':
        return <TradingStockExchange />;
      case 'TRADING:BOLSA:DETAILS':
        return <TradingStockExchangeDetails />;
      case 'MARKET:PRIMARY:PROOF':
      case 'MARKET:BOLSA:PROOF':
      case 'MARKET:BOLSA:DETAILS:PROOF':
      case 'MARKET:FUNDS:PROOF':
      case 'MARKET:FUNDS:DETAILS:PROOF':
      case 'TRADING:PRIMARY:PROOF':
      case 'TRADING:BOLSA:PROOF':
      case 'TRADING:BOLSA:DETAILS:PROOF':
      case 'TRADING:FUNDS:PROOF':
      case 'TRADING:FUNDS:DETAILS:PROOF':
        return <TradingProofPreview />;
      case 'TRADING:FUNDS':
        return <TradingFunds />;
      case 'TRADING:FUNDS:DETAILS':
        return <TradingFundsDetails />;
      case 'WALLET':
        return <Wallet />;
      case 'SETTINGS':
      case 'GENERIC:SETTINGS':
        return <Settings />;
      case 'GENERIC:SETTINGS:ACCOUNTS':
        return <ConfigureAccounts />;
      case 'GENERIC:SETTINGS:EMAIL':
        return <Email />;
      case 'GENERIC:SETTINGS:CONTACT':
        return <Contact />;
      case 'GENERIC:SETTINGS:OTPDELIVMETH':
        return <OTPDeliveryMethod />;
      case 'GENERIC:SETTINGS:PASSWORD':
        return <ChangePassword />;
      case 'GENERIC:SETTINGS:LANGUAGE':
        return <ChooseLanguage />;
      case 'GENERIC:SETTINGS:SUPPORT':
        return <CustomerSupport />;
      case 'GENERIC:SETTINGS:QUESTIONNAIRE':
        return <QuestionnaireMobile />;

      default:
        return <LandingPage />;
    }
  };

  useEffect(() => {
    if ('serviceWorker' in navigator && isUserAuthenticated) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update();
      });
    }
  }, [location, isUserAuthenticated]);

  return (
    <Suspense fallback={<div />}>
      <Routes>
        <Route path="/Login" element={<Login />} />
        <Route path="/Onboarding" element={<Onboarding />} />
        <Route path="/Dashboard" element={<Dashboard />} />
        <Route path="/Market/Primary" element={<MarketPrimary />} />
        <Route
          path="/Market/Primary/ProofPreview"
          element={<TradingProofPreview />}
        />
        <Route
          path="/Market/StockExchange"
          element={
            <TitlesViewContextProvider>
              <MarketStockExchange />
            </TitlesViewContextProvider>
          }
        />
        <Route
          path="/Market/StockExchange/ProofPreview"
          element={<TradingProofPreview />}
        />
        <Route
          path="/Market/StockExchange/Details"
          element={<TrlStockExchangeMarketDetails />}
        />
        <Route
          path="/Market/StockExchange/Details/ProofPreview"
          element={<TradingProofPreview />}
        />
        <Route path="/Market/Funds" element={<Funds />} />
        <Route
          path="/Market/Funds/ProofPreview"
          element={<TradingProofPreview />}
        />
        <Route
          path="/Market/Funds/Details"
          element={<TrlFundsManagerDetails />}
        />
        <Route
          path="/Market/Funds/Details/ProofPreview"
          element={<TradingProofPreview />}
        />
        <Route path="/Trading/Primary" element={<TradingPrimary />} />
        <Route
          path="/Trading/Primary/ProofPreview"
          element={<TradingProofPreview />}
        />
        <Route
          path="/Trading/StockExchange"
          element={<TradingStockExchange />}
        />
        <Route
          path="/Trading/StockExchange/ProofPreview"
          element={<TradingProofPreview />}
        />
        <Route
          path="/Trading/StockExchange/Details"
          element={<TradingStockExchangeDetails />}
        />
        <Route
          path="/Trading/StockExchange/Details/ProofPreview"
          element={<TradingProofPreview />}
        />
        <Route path="/Trading/Funds" element={<TradingFunds />} />
        <Route
          path="/Trading/Funds/ProofPreview"
          element={<TradingProofPreview />}
        />
        <Route
          path="/Trading/Funds/Details"
          element={<TradingFundsDetails />}
        />
        <Route
          path="/Trading/Funds/Details/ProofPreview"
          element={<TradingProofPreview />}
        />
        <Route path="/Wallet" element={<Wallet />} />

        <Route
          path="/Insert/Order/Primary"
          element={<TrlInsertOrderPrimary />}
        />

        <Route
          path="/Market/Funds/NewOrder"
          element={<TrlInsertOrderPrimary />}
        />

        <Route path="/Settings" element={<Settings />} />
        <Route
          path="/Settings/ConfigureAccounts"
          element={<ConfigureAccounts />}
        />
        <Route path="/Settings/Email" element={<Email />} />
        <Route path="/Settings/Contact" element={<Contact />} />
        <Route
          path="/Settings/OTPDelivMethod"
          element={<OTPDeliveryMethod />}
        />
        <Route path="/Settings/ChangePassword" element={<ChangePassword />} />
        <Route
          path="/Settings/Questionnaire"
          element={<QuestionnaireMobile />}
        />
        <Route path="/Settings/ChooseLanguage" element={<ChooseLanguage />} />
        <Route path="/Settings/CustomerSupport" element={<CustomerSupport />} />
        <Route exact={false} path="/" element={controlF5Redirect()} />
      </Routes>
    </Suspense>
  );
};
